import type {
  ParticipantTaskTemplate,
  UpdateParticipantTaskRequest,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { dateTimeSchema, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateParticipantTaskTemplateMutation } from '../../../graphql/hooks/useUpdateParticipantTaskTemplateMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantTaskTemplateForm } from './ParticipantTaskTemplateForm';

type FormValues = UpdateParticipantTaskRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string().min(1, 'Task name is required'),
  requiresDueDate: z.boolean(),
  dueDate: dateTimeSchema.optional(),
  labelIds: z.array(z.string()),
});

interface ParticipantNoteEditModalProps {
  taskTemplate: ParticipantTaskTemplate;
}

export const ParticipantTaskTemplateEditModal = createModal<ParticipantNoteEditModalProps>(
  (props) => {
    const { taskTemplate, modal } = props;
    const { errorToast, successToast, organization } = useAppContext();

    const { hide, remove, visible } = modal;

    const mutation = useUpdateParticipantTaskTemplateMutation();

    const methods = useForm<FormValues>({
      defaultValues: {
        name: taskTemplate.name,
        requiresDueDate: taskTemplate.requiresDueDate,
        labelIds: taskTemplate.labels.map((label) => label.id),
      },
      mode: 'all',
      resolver: zodResolver(schema),
    });

    const onSubmit = React.useCallback(
      async (values: FormValues) => {
        await mutation
          .mutateAsync({
            organizationId: organization.id,
            participantTaskTemplateId: taskTemplate.id,
            request: values,
          })
          .then(() => {
            successToast('Task template updated successfully');
            hide();
          })
          .catch((error) => {
            errorToast(`Error updating task template: ${error.message}`);
          });
      },
      [errorToast, hide, mutation, organization.id, taskTemplate, successToast],
    );

    return (
      <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
        <ChakraV3.Dialog.Backdrop />
        <ChakraV3.Dialog.Positioner>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <ChakraV3.Dialog.Content>
                <ChakraV3.DialogTitleSubtitle
                  title='Edit a task template'
                  subtitle='Please complete the following to proceed'
                />
                <ChakraV3.Dialog.Body>
                  <ParticipantTaskTemplateForm />
                </ChakraV3.Dialog.Body>
                <ChakraV3.Dialog.Footer>
                  <ChakraV3.HStack>
                    <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                      Close
                    </ChakraV3.Button>
                    <HookedSubmitButton>Save</HookedSubmitButton>
                  </ChakraV3.HStack>
                </ChakraV3.Dialog.Footer>
              </ChakraV3.Dialog.Content>
            </form>
          </FormProvider>
        </ChakraV3.Dialog.Positioner>
      </ChakraV3.Dialog.Root>
    );
  },
);
